import { lazy, Suspense, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Redirect, Route, Switch } from "wouter";
import { Shell } from "./components/Shell";
import { LoadingPanel } from "./components/Ui";
import { isDemoMode, seoApi } from "./lib/api";

const Login = lazy(() => import("./pages/Login"));
const Overview = lazy(() => import("./pages/Overview"));
const Rankings = lazy(() => import("./pages/Rankings"));
const Traffic = lazy(() => import("./pages/Traffic"));
const Matrix = lazy(() => import("./pages/Matrix"));
const Movers = lazy(() => import("./pages/Movers"));
const SyncStatus = lazy(() => import("./pages/SyncStatus"));
const UrlPerformance = lazy(() => import("./pages/UrlPerformance"));
const UrlDetail = lazy(() => import("./pages/UrlDetail"));

export default function App() {
  const queryClient = useQueryClient();
  const session = useQuery({
    queryKey: ["session"],
    queryFn: seoApi.session,
    retry: false,
    staleTime: 5 * 60_000
  });
  useEffect(() => {
    const expire = () => {
      queryClient.clear();
      queryClient.setQueryData(["session"], { authenticated: false });
    };
    window.addEventListener("agrowth-auth-expired", expire);
    return () => window.removeEventListener("agrowth-auth-expired", expire);
  }, [queryClient]);

  if (session.isPending) return <LoadingPanel />;
  if (!isDemoMode && (session.isError || !session.data?.authenticated)) {
    return (
      <Suspense fallback={<LoadingPanel />}>
        <Login
          onLogin={async (password) => {
            await seoApi.login(password);
            queryClient.setQueryData(["session"], { authenticated: true });
          }}
        />
      </Suspense>
    );
  }

  return (
    <Shell
      onLogout={async () => {
        await seoApi.logout();
        queryClient.clear();
        queryClient.setQueryData(["session"], { authenticated: false });
      }}
    >
      <Suspense fallback={<LoadingPanel />}>
        <Switch>
          <Route path="/" component={UrlPerformance} />
          <Route path="/overview" component={Overview} />
          <Route path="/url-performance/:id" component={UrlDetail} />
          <Route path="/rankings" component={Rankings} />
          <Route path="/traffic" component={Traffic} />
          <Route path="/ranking-traffic" component={Matrix} />
          <Route path="/movers" component={Movers} />
          <Route path="/sync" component={SyncStatus} />
          <Route><Redirect to="/" /></Route>
        </Switch>
      </Suspense>
    </Shell>
  );
}
